import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemIcon,
  Chip,
  FormHelperText,
  RadioGroup,
  Radio,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const DEFAULT_OBJECTIVE = 'OUTCOME_LEADS';

const CampaignConfigurator = ({ campaignData, onCampaignChange, selectedPage, facebookPages }) => {
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [displayUrl, setDisplayUrl] = useState('yourdomain.com');
  const [language, setLanguage] = useState('en'); // Default to English
  const [startTiming, setStartTiming] = useState('delayed'); // 'immediate' or 'delayed' - default to delayed
  
  // Special Ad Categories options (updated for 2024-2025)
  const specialAdCategories = [
    { value: 'NONE', label: 'None', description: 'No special category' },
    { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Financial Products & Services', description: 'Ads related to credit cards, loans, insurance, investment advice, or other financial services' },
    { value: 'EMPLOYMENT', label: 'Employment', description: 'Ads related to job opportunities or employment' },
    { value: 'HOUSING', label: 'Housing', description: 'Ads related to housing, real estate, or mortgage' },
    { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Social issues, elections or politics', description: 'Ads about social issues, elections, or politics' },
  ];
  
  // Default Facebook pages if not provided
  const defaultFacebookPages = [
    {
      id: '524097567461962',
      name: 'Expected Unexpected',
      description: 'Facebook page for surprising and unexpected content'
    },
    {
      id: '464195180106831',
      name: 'Explore Now',
      description: 'Facebook page for exploration and discovery content'
    },
    {
      id: '308888415633103',
      name: 'Top Local Service Deals',
      description: 'Facebook page for local service campaigns'
    },
    {
      id: '667805466407711',
      name: 'Anything Related',
      description: 'General purpose Facebook page for diverse content'
    },
    {
      id: '660501717154707',
      name: 'Ask anything',
      description: 'Facebook page for Q&A and general inquiries'
    },
    {
      id: '703053446232156',
      name: 'Mildly Interesting',
      description: 'Facebook page for mildly interesting and engaging content'
    },
    {
      id: '842298358962790',
      name: 'Knowledge Hub',
      description: 'Facebook page for educational and informative content'
    }
  ];
  
  const availablePages = facebookPages || defaultFacebookPages;

  // Set default objective, language, status, and timing on mount
  useEffect(() => {
    const updates = {};
    let hasUpdates = false;
    
    if (!campaignData.objective) {
      updates.objective = DEFAULT_OBJECTIVE;
      hasUpdates = true;
    }
    
    if (!campaignData.language) {
      updates.language = 'en'; // Default to English
      hasUpdates = true;
    }

    // Always set campaign to ACTIVE by default (remove PAUSED option)
    if (!campaignData.status || campaignData.status === 'PAUSED') {
      updates.status = 'ACTIVE';
      hasUpdates = true;
    }

    // Set default start timing to delayed with calculated start time (next day at midnight UTC)
    if (!campaignData.startTiming) {
      // Get current time in UTC and add exactly 24 hours to ensure we get tomorrow
      const nowUTC = new Date();
      const tomorrowUTC = new Date(nowUTC.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours
      // Set to midnight UTC
      tomorrowUTC.setUTCHours(0, 0, 0, 0);
      
      updates.startTiming = 'delayed';
      updates.start_time = tomorrowUTC.toISOString();
      hasUpdates = true;
      
      // Log for debugging
      console.log('Setting default delayed start time:', {
        nowUTC: nowUTC.toISOString(),
        tomorrowUTC: tomorrowUTC.toISOString(),
        unixTimestamp: Math.floor(tomorrowUTC.getTime() / 1000)
      });
    }
    
    if (hasUpdates) {
      onCampaignChange({
        ...campaignData,
        ...updates,
      });
    }
  }, []);

  // Handle language change
  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    onCampaignChange({
      ...campaignData,
      language: newLanguage,
    });
  };

  // Update display URL when page changes
  useEffect(() => {
    if (selectedPage) {
      setDisplayUrl(getInitialDisplayUrl());
    }
  }, [selectedPage]);

  // Generate initial display URL from the first keyword
  const getInitialDisplayUrl = () => {
    if (!selectedPage) return 'yourdomain.com';
    
    // Get the first keyword
    let baseUrl = 'yourdomain';
    
    // Handle both array and string formats of keywords
    if (selectedPage.keywords) {
      let firstKeyword;
      
      if (Array.isArray(selectedPage.keywords) && selectedPage.keywords.length > 0) {
        // It's already an array, use the first element
        firstKeyword = selectedPage.keywords[0];
      } else if (typeof selectedPage.keywords === 'string') {
        // It's a comma-separated string, split it and take the first one
        firstKeyword = selectedPage.keywords.split(',')[0].trim();
      }
      
      if (firstKeyword) {
        baseUrl = firstKeyword
          .replace(/\s+/g, '') // Remove all whitespace
          .replace(/[^a-zA-Z0-9]/g, '') // Remove special characters
          .toLowerCase();
      }
    }
    
    return `${baseUrl}.com`;
  };

  // Handle display URL change
  const handleDisplayUrlChange = (event) => {
    setDisplayUrl(event.target.value);
  };

  // Handle campaign name change
  const handleNameChange = (event) => {
    onCampaignChange({
      ...campaignData,
      name: event.target.value,
    });
  };


  // Handle Facebook page change
  const handleFacebookPageChange = (event) => {
    onCampaignChange({
      ...campaignData,
      facebookPageId: event.target.value,
    });
  };

  // Handle special ad categories change
  const handleSpecialAdCategoriesChange = (event) => {
    const value = event.target.value;
    // Filter out 'NONE' and only keep actual categories
    const categories = value.filter(cat => cat !== 'NONE');
    
    onCampaignChange({
      ...campaignData,
      specialAdCategories: categories,
    });
  };

  // Handle start timing change
  const handleStartTimingChange = (event) => {
    const newTiming = event.target.value;
    setStartTiming(newTiming);
    
    // Calculate start_time based on selection
    let startTime = null;
    if (newTiming === 'delayed') {
      // Set to next day at midnight UTC for consistency
      const nowUTC = new Date();
      const tomorrowUTC = new Date(nowUTC.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours
      // Set to midnight UTC
      tomorrowUTC.setUTCHours(0, 0, 0, 0);
      startTime = tomorrowUTC.toISOString();
      
      // Log for debugging
      console.log('Setting delayed start time:', {
        nowUTC: nowUTC.toISOString(),
        tomorrowUTC: tomorrowUTC.toISOString(),
        unixTimestamp: Math.floor(tomorrowUTC.getTime() / 1000),
        localDisplay: tomorrowUTC.toLocaleString()
      });
    }
    
    onCampaignChange({
      ...campaignData,
      startTiming: newTiming,
      start_time: startTime,
    });
  };

  // Get formatted display time for delayed start
  const getDelayedStartTime = () => {
    if (startTiming === 'delayed') {
      const nowUTC = new Date();
      const tomorrowUTC = new Date(nowUTC.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours
      // Set to midnight UTC
      tomorrowUTC.setUTCHours(0, 0, 0, 0);
      return tomorrowUTC.toLocaleString(); // Shows in user's local timezone for display
    }
    return null;
  };

  // Generate the destination URL based on selected page
  const getDestinationUrl = () => {
    if (!selectedPage) return 'Select a page first to see destination URL';
    
    let keywordsStr = '';
    
    // Handle both array and string formats for keywords
    if (Array.isArray(selectedPage.keywords)) {
      keywordsStr = selectedPage.keywords.join(',');
    } else if (typeof selectedPage.keywords === 'string') {
      keywordsStr = selectedPage.keywords;
    }
    
    // Encode keywords, replacing spaces with %20
    const encodedKeywords = keywordsStr.replace(/ /g, '%20');
    
    // Use selected language (en or es)
    const langParam = language === 'es' ? 'es' : 'en';
    
    return `https://www.thecozyguide.com/dsr/${selectedPage.slug || ''}/?adx_title=${encodeURIComponent(selectedPage.title || '')}&adx_publisher_id=68&utm_source=facebook&adid={{ad.id}}&adx_terms=${encodedKeywords}&adx_lang=${langParam}&cmpgid={{campaign.id}}`;
  };

  // Handle copying URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(getDestinationUrl());
    setCopySnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setCopySnackbar(false);
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Campaign Name */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Campaign Name"
            value={campaignData.name}
            onChange={handleNameChange}
            helperText={
              !campaignData.name
                ? 'Campaign name is required'
                : 'A descriptive name for your campaign'
            }
            error={!campaignData.name}
            required
          />
        </Grid>

        {/* Facebook Page Selection */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel id="facebook-page-label">Facebook Page</InputLabel>
            <Select
              labelId="facebook-page-label"
              id="facebook-page-select"
              value={campaignData.facebookPageId || ''}
              label="Facebook Page"
              onChange={handleFacebookPageChange}
            >
              {availablePages.map((page) => (
                <MenuItem key={page.id} value={page.id}>
                  {page.name}
                </MenuItem>
              ))}
            </Select>
            {campaignData.facebookPageId && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, px: 1.75 }}>
                {availablePages.find(p => p.id === campaignData.facebookPageId)?.description}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Language Selection */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              labelId="language-label"
              id="language-select"
              value={language}
              label="Language"
              onChange={handleLanguageChange}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
            </Select>
            <FormHelperText>Select the language for your campaign</FormHelperText>
          </FormControl>
        </Grid>

        {/* Special Ad Categories */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="special-ad-categories-label">Special Ad Categories</InputLabel>
            <Select
              labelId="special-ad-categories-label"
              id="special-ad-categories-select"
              multiple
              value={campaignData.specialAdCategories || []}
              onChange={handleSpecialAdCategoriesChange}
              label="Special Ad Categories"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.length === 0 ? (
                    <Chip size="small" label="None" />
                  ) : (
                    selected.map((value) => (
                      <Chip 
                        key={value} 
                        size="small" 
                        label={specialAdCategories.find(cat => cat.value === value)?.label || value} 
                      />
                    ))
                  )}
                </Box>
              )}
            >
              {specialAdCategories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  <Checkbox checked={(campaignData.specialAdCategories || []).indexOf(category.value) > -1} />
                  <ListItemText 
                    primary={category.label}
                    secondary={category.description}
                  />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" />
                Select if your ads fall under regulated categories. This affects targeting options.
              </Box>
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Destination URL Display */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Destination URL
              </Typography>
              <Tooltip title="Copy URL">
                <IconButton onClick={copyToClipboard} size="small">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Display URL - what users will see in the ads - now editable */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Display URL (shown in ad):
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={displayUrl}
                onChange={handleDisplayUrlChange}
                placeholder="yourdomain.com"
                helperText="Customize the URL that appears in your ad"
                sx={{ 
                  '& .MuiInputBase-root': { 
                    bgcolor: 'background.paper'
                  }
                }}
              />
            </Box>
            
            {/* Actual destination URL */}
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Actual destination URL:
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              multiline
              rows={4}
              value={getDestinationUrl()}
              InputProps={{ readOnly: true }}
              sx={{ 
                '& .MuiInputBase-root': { 
                  bgcolor: 'background.paper',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                  overflowX: 'auto'
                }
              }}
            />
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This is the landing page URL where your ad will direct visitors
            </Typography>
          </Paper>
        </Grid>

        {/* Start Timing Options */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Campaign Start Time
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={startTiming}
                onChange={handleStartTimingChange}
                row
              >
                <FormControlLabel
                  value="immediate"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Start immediately</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Campaign will begin as soon as it's created
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="delayed"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">Delayed start (next day at 00:00 UTC)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Campaign will start tomorrow at midnight UTC
                        {getDelayedStartTime() && (
                          <span> (Your local time: {getDelayedStartTime()})</span>
                        )}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>

        {/* Campaign Configuration Info */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Campaign Configuration
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Objective"
                  secondary="Lead Generation - Collect leads for your business"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Special Ad Categories"
                  secondary={
                    campaignData.specialAdCategories && campaignData.specialAdCategories.length > 0
                      ? campaignData.specialAdCategories
                          .map(cat => specialAdCategories.find(c => c.value === cat)?.label || cat)
                          .join(', ')
                      : 'None - Standard targeting available'
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Campaign Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Campaign Summary
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Name"
                  secondary={campaignData.name || 'Not set'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Status"
                  secondary={
                    <Box
                      component="span"
                      sx={{
                        color: 'success.main',
                        fontWeight: 'medium',
                      }}
                    >
                      Active
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Start Time"
                  secondary={
                    startTiming === 'immediate' 
                      ? 'Immediately after creation'
                      : `Delayed start tomorrow at midnight UTC (Your local time: ${getDelayedStartTime()})`
                  }
                />
              </ListItem>
              {selectedPage && (
                <ListItem>
                  <ListItemText
                    primary="Content Page"
                    secondary={selectedPage.title || 'No title'}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary="Facebook Page"
                  secondary={availablePages.find(p => p.id === campaignData.facebookPageId)?.name || 'Not selected'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Display URL"
                  secondary={displayUrl}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Special Ad Categories"
                  secondary={
                    campaignData.specialAdCategories && campaignData.specialAdCategories.length > 0
                      ? campaignData.specialAdCategories
                          .map(cat => specialAdCategories.find(c => c.value === cat)?.label || cat)
                          .join(', ')
                      : 'None'
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar 
        open={copySnackbar} 
        autoHideDuration={3000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          URL copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignConfigurator; 